import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { CookieConsent } from "@/components/CookieConsent";
import {
  CinematicHero,
  SectionHead,
  Reveal,
  FullBleedQuote,
  SplitFeature,
  NumberedSteps,
  StatStripe,
  CinematicCTA,
  CinematicFAQ,
  TripleStrip,
} from "@/components/service/Cinematic";
import heroImg from "@/assets/special-surface.jpg";
import s1 from "@/assets/surface-ref-1.webp";
import s2 from "@/assets/surface-ref-2.webp";
import s3 from "@/assets/surface-ref-3.jpg";
import s4 from "@/assets/surface-ref-4.jpg";
import s5 from "@/assets/surface-ref-5.jpeg";
import s6 from "@/assets/surface-ref-6.jpeg";
import f1 from "@/assets/floor-ref-1.jpeg";
import f2 from "@/assets/floor-ref-2.jpg";
import f3 from "@/assets/floor-ref-3.webp";

const techniken = [
  {
    title: "Betonoptik",
    subtitle: "Urban, roh, tiefenwirksam",
    text: "Mikrozement-Look in Grauschattierungen, Strukturtiefen und Schichtsystemen. Jede Fläche ein Unikat – täuschend echt, aber leichter und vielseitiger als realer Beton.",
  },
  {
    title: "Marmorino & Kalkspachtel",
    subtitle: "Venezianisch, seidig, zeitlos",
    text: "Naturkalkputz mit Marmormehl, maschinell geglättet und poliert. Seidiger, tiefer Glanz – Marmor täuschend ähnlich, atmungsaktiv und feuchteregulierend.",
  },
  {
    title: "Metalloptik",
    subtitle: "Kupfer, Bronze, Gold",
    text: "Pigmente und Spachteltechnik erzeugen ein dreidimensionales, schimmerndes Finish. Königsdisziplin der Wandgestaltung für Wohnräume, Empfänge und Gastronomie.",
  },
  {
    title: "Lasur & Effektputz",
    subtitle: "Transparent, strukturiert, individuell",
    text: "Lasuren geben Wänden Tiefe wie ein Blick in Bernstein. Travertin, Craquelé, Baumwoll- und Seidenputze erweitern das Repertoire für jeden Stil.",
  },
  {
    title: "Oikos Ottocento",
    subtitle: "Italienisch, changierend, samtig",
    text: "Exklusive dekorative Spachteltechnik mit charakteristisch changierendem Effekt. Samtige Struktur, subtile Lichtreflexe und eine harmonisch-wolkige Oberfläche – emissionsarm und atmungsaktiv.",
  },
];

const steps = [
  { title: "Ideenfindung & Musterflächen", desc: "Wir besprechen Räume, Licht und Stil – und zeigen Ihnen reale Musterflächen in Herford. Materialien spürt man besser, als man sie beschreiben kann." },
  { title: "Bemusterung vor Ort", desc: "Ausgewählte Techniken werden direkt an Ihrer Wand bemustert. So sehen Sie Farbe und Struktur im echten Lichtspiel." },
  { title: "Untergrundvorbereitung", desc: "Spachteln, Glätten, Grundieren. Edle Oberflächen leben vom perfekten Untergrund – hier entscheidet sich das Endergebnis." },
  { title: "Schicht für Schicht", desc: "Auftrag in mehreren Lagen mit Spachtel, Kelle und Polierwerkzeug. Jede Bewegung sitzt – das macht den Unterschied zum Industrieprodukt." },
  { title: "Politur & Versiegelung", desc: "Finish je nach System: Wachs, Seife oder Polyurethan. Schutz gegen Wasser, Fett und mechanische Belastung – ohne den Charakter zu verlieren." },
  { title: "Übergabe & Pflegehinweis", desc: "Saubere Endübergabe mit Pflegeanleitung. Wir bleiben Ansprechpartner – auch Jahre später für Nachpflege oder Reparatur." },
];

const boeden = [
  {
    title: "Mikrozement­boden",
    subtitle: "Fugenlos, warm, zeitlos",
    text: "2–3 mm dünner Aufbau auf nahezu jedem Untergrund – auch über bestehenden Fliesen. Wasserdicht versiegelt für Wohnen, Küche und Bad.",
  },
  {
    title: "Spachtel- & Designboden",
    subtitle: "Loftartig, hart, fugenlos",
    text: "Industriecharakter für offene Wohn- und Essbereiche. In Grautönen oder pigmentiert. Hart, robust, dauerhaft.",
  },
];

const faqs = [
  { q: "Was kostet Betonoptik oder Marmorino in Herford?", a: "Eine handwerkliche Spachteltechnik auf 12–20 m² Wand liegt je nach System und Aufwand bei 800–3.000 €. Marmorino und Metalloptik kosten als Unikate mehr als einfachere Spachtelsysteme. Wir zeigen Musterflächen und kalkulieren einen verbindlichen Festpreis." },
  { q: "Kann Mikrozement über bestehende Fliesen?", a: "In den meisten Fällen ja. Voraussetzung: Fliesen sitzen fest, sind rissfrei und der Untergrund ist eben vorzubereiten. Das spart Abriss, Lärm und Schmutz. Wir prüfen das vor Ort." },
  { q: "Wie pflegeleicht sind edle Oberflächen?", a: "Nach korrekter Versiegelung sehr robust und alltagstauglich. Reinigung mit pH-neutralen Mitteln, gelegentliche Nachpflege je nach Versiegelung. Marmorino und Kalkspachtel sind zudem atmungsaktiv und feuchteregulierend." },
  { q: "Wie lange dauert die Umsetzung?", a: "Eine Wand in Spachteltechnik: 2–4 Werktage inkl. Trocknung. Mikrozement-Boden je nach Fläche 5–10 Werktage. Wir takten alle Schichten so, dass Sie schnell zurück in den Raum können." },
];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Edle Oberflächen & Designböden",
  name: "Edle Oberflächen & Designböden Herford",
  provider: { "@type": "LocalBusiness", name: "Malerfachbetrieb Stein GmbH & Co. KG" },
  areaServed: "Herford",
  description: "Betonoptik, Marmorino, Metalloptik und fugenlose Designböden in Herford – handgefertigte Unikate vom Malermeister.",
};

const EdleOberflaechenHerford = () => (
  <div className="min-h-screen bg-background">
    <SEO
      title="Designböden in Herford: Edle Oberflächen, fugenlos schön"
      description="Edle Oberflächen & Designböden in Herford: Betonoptik, Marmorino, Metalloptik & fugenlose Böden – handgefertigte Unikate vom Malermeister. ☎ 05221 3832130"
      keywords="edle Oberflächen Herford, Designboden Herford, Betonoptik Herford, Marmorino Herford, Mikrozement Boden Herford, Spachteltechnik Herford, Metalloptik Wand Herford"
      canonical="https://malerfachbetrieb-stein.de/edle-oberflaechen-designboeden-herford"
      structuredData={structuredData}
    />
    <Navigation />
    <WhatsAppButton />

    <main>
      <CinematicHero
        chapter="Kapitel 06 / Oberflächen"
        eyebrow="Edle Oberflächen & Designböden"
        title={<>Wände als <em className="not-italic text-background/70">Unikat</em>.</>}
        subtitle="Betonoptik, Marmorino, Metalloptik und fugenlose Böden – handgefertigte Oberflächen, die kein Tapetenmuster und keine Fototapete kopieren kann."
        image={heroImg}
        imageAlt="Edle Oberflächen Herford – Spachteltechnik in Betonoptik"
        ctaLabel="Gestaltungsberatung anfragen"
      />

      <StatStripe
        stats={[
          { value: "100%", label: "Unikat pro Wand" },
          { value: "50+", label: "Farb- & Strukturwelten" },
          { value: "2–3 mm", label: "Mikrozement-Aufbau" },
          { value: "10+", label: "Jahre Haltbarkeit" },
        ]}
      />

      <FullBleedQuote
        image={s2}
        imageAlt="Spachteltechnik Betonoptik Detail"
        quote={<>„Eine Wand kann mehr sein als Hintergrund – sie kann der Charakter eines Raumes sein.“</>}
        caption="Handwerk als Material"
      />

      {/* Techniken split */}
      <section className="py-16 md:py-24 lg:py-32">
        <div className="container mx-auto">
          <SectionHead
            eyebrow="Techniken"
            title={<>Vier Wege zur<br /> eigenen Wand.</>}
          />
          <div className="space-y-24 md:space-y-32">
            <SplitFeature
              index="01"
              eyebrow="Betonoptik"
              title="Industriecharakter in Spachtel­technik."
              body="Mikrozement-Look in Grauschattierungen und Strukturtiefen. Wir realisieren die Optik von rohem Sichtbeton – aber leichter, vielseitiger und an jeder Wand möglich."
              bullets={["Mehrschicht-Aufbau", "Strukturtiefen nach Wunsch", "Auch über Fliesen", "Versiegelt & robust"]}
              image={s1}
              imageAlt="Wandgestaltung Betonoptik"
            />
            <SplitFeature
              reverse
              index="02"
              eyebrow="Marmorino"
              title="Venezianische Kalk­spachtel­technik."
              body="Naturkalkputz mit Marmormehl – maschinell geglättet, von Hand poliert. Seidiger Tiefenglanz, atmungsaktiv und feuchteregulierend. Eine Technik mit Geschichte."
              image={s3}
              imageAlt="Marmorino Kalkspachtel Wand"
            />
            <SplitFeature
              index="03"
              eyebrow="Metalloptik"
              title="Kupfer, Bronze, Gold – aufgespachtelt."
              body="Spezielle Pigmente und Spachtelbewegung erzeugen ein dreidimensionales, schimmerndes Finish. Wirkung im Raum: völlig anders als auf jedem Foto."
              image={s4}
              imageAlt="Wand in Metalloptik"
            />
            <SplitFeature
              reverse
              index="04"
              eyebrow="Lasur & Effektputz"
              title="Transparenz, Struktur, Tiefe."
              body="Lasuren geben Wänden Tiefe wie Bernstein. Travertin, Craquelé, Baumwoll- und Seidenputze erweitern das Repertoire – für jeden Stil das passende Finish."
              image={s5}
              imageAlt="Lasur und Effektputz Wand"
            />
            <SplitFeature
              index="05"
              eyebrow="Oikos Ottocento"
              title="Italienische Eleganz mit changierendem Effekt."
              body="Oikos Ottocento ist eine hochwertige dekorative Spachteltechnik für exklusive Wandgestaltungen im Innenbereich. Ihr charakteristisch changierender Effekt verleiht jeder Oberfläche besondere Tiefe und Eleganz – eine Mischung aus samtiger Struktur, subtilen Lichtreflexen und harmonisch-wolkiger Optik, die an feinste italienische Wandveredelungen erinnert. Emissionsarm, umweltfreundlich und atmungsaktiv – ideal für anspruchsvolle Wohn- und Geschäftsräume, ob als Akzentwand oder flächige Gestaltung."
              bullets={["Changierender Tiefeneffekt", "Samtige Oberfläche", "Emissionsarm & atmungsaktiv", "Für Wohn- & Geschäftsräume"]}
              image={s6}
              imageAlt="Oikos Ottocento Spachteltechnik Wand"
            />
          </div>
        </div>
      </section>

      {/* Böden – dark */}
      <section className="py-16 md:py-24 lg:py-32 bg-foreground text-background">
        <div className="container mx-auto">
          <SectionHead
            invert
            eyebrow="Designböden"
            title="Fugenlose Böden für offenes Wohnen."
            intro="Mikrozement und Spachtelboden – fugenlos verlegt, individuell pigmentiert, dauerhaft versiegelt. Der Boden als ruhige, durchgehende Fläche."
          />
          <div className="grid md:grid-cols-2 gap-px bg-background/10">
            {boeden.map((b, i) => (
              <Reveal key={b.title} delay={i * 100}>
                <div className="bg-foreground p-8 md:p-12 h-full">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-background/50 mb-3">
                    {String(i + 1).padStart(2, "0")} / {b.subtitle}
                  </div>
                  <h3 className="font-caslon text-3xl md:text-4xl font-bold mb-5">{b.title}</h3>
                  <p className="text-background/70 leading-relaxed">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FullBleedQuote
        image={f3}
        imageAlt="Designboden fugenlos in Spachteltechnik"
        quote={<>„Ein guter Boden verbindet die Räume – und beruhigt das ganze Haus.“</>}
        caption="Fugenlos gedacht"
        height="medium"
      />

      {/* Materialien-Grid */}
      <section className="py-16 md:py-24 lg:py-32">
        <div className="container mx-auto">
          <SectionHead
            eyebrow="Repertoire"
            title="Spachteltechniken im Überblick."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-foreground/10 border border-foreground/10">
            {techniken.map((t, i) => (
              <Reveal key={t.title} delay={i * 80}>
                <div className="bg-background p-8 md:p-10 h-full">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">
                    {String(i + 1).padStart(2, "0")} / {t.subtitle}
                  </div>
                  <h3 className="font-caslon text-2xl md:text-3xl font-bold mb-4">{t.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Ablauf */}
      <section className="py-16 md:py-24 lg:py-32 bg-muted/20">
        <div className="container mx-auto">
          <SectionHead
            eyebrow="Ablauf"
            title="So entsteht Ihre Oberfläche."
          />
          <NumberedSteps steps={steps} />
        </div>
      </section>

      <TripleStrip
        images={[
          { src: s6, alt: "Spachteltechnik Referenz", caption: "Marmorino poliert" },
          { src: f1, alt: "Bodenverlegen Referenz", caption: "Bodenverlegen" },
          { src: f2, alt: "Bodenverlegen Referenz", caption: "Bodenverlegen" },
        ]}
      />

      <CinematicFAQ
        faqs={faqs}
        title="Fragen zu edlen Oberflächen."
      />

      <CinematicCTA
        image={s2}
        imageAlt="Detail Spachteltechnik"
        title={<>Zeigen Sie uns<br /> Ihre Idee.</>}
        body="Kostenlose Erstberatung mit Musterflächen – direkt bei Ihnen oder in unserem Showroom in Herford."
        ctaLabel="Gestaltungsberatung anfragen"
      />
    </main>

    <Footer />
    <CookieConsent />
  </div>
);

export default EdleOberflaechenHerford;
